import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, Send, Link2, Eye, Archive, ChevronDown, ChevronRight,
  Loader2, Check, X, Copy, FileText, BarChart3, Mail, RefreshCw,
} from "lucide-react";

const Q_TYPES = [
  { key: "short_text",    label: "Short text" },
  { key: "long_text",     label: "Long text" },
  { key: "single_choice", label: "Single choice" },
  { key: "multi_choice",  label: "Multi choice" },
  { key: "rating",        label: "Rating 1-5" },
  { key: "yes_no",        label: "Yes / No" },
  { key: "number",        label: "Number" },
  { key: "date",          label: "Date" },
];

/** Collapsible Surveys section nested inside each Campaign card. */
export default function CampaignSurveysSection({ campaign }) {
  const [open, setOpen] = useState(false);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [responsesFor, setResponsesFor] = useState(null);
  const [invitationsFor, setInvitationsFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/campaigns/${campaign.id}/surveys`);
      setSurveys(data.surveys || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load surveys");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const publish = async (id) => {
    try {
      const { data } = await api.post(`/surveys/${id}/publish`);
      await navigator.clipboard?.writeText(data.public_url).catch(() => {});
      toast.success("Published. Public link copied to clipboard.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };

  const archive = async (id) => {
    if (!window.confirm("Archive this survey? Respondents will no longer be able to access it.")) return;
    try {
      await api.post(`/surveys/${id}/archive`);
      toast.success("Survey archived");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this survey and ALL its responses? This cannot be undone.")) return;
    try {
      await api.delete(`/surveys/${id}`);
      toast.success("Survey deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const copyLink = async (s) => {
    if (!s.public_token) { toast.error("Publish the survey first"); return; }
    const url = `${window.location.origin}/survey/${s.public_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied");
    } catch { toast.message(url); }
  };

  return (
    <div className="border-t border-border mt-4 pt-3">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`campaign-surveys-toggle-${campaign.id}`}
        className="text-xs font-semibold uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
      >
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        <FileText size={11} /> Surveys {surveys.length > 0 && <span className="text-primary">· {surveys.length}</span>}
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <button
            onClick={() => { setEditingId(null); setEditorOpen(true); }}
            data-testid={`campaign-survey-new-${campaign.id}`}
            className="btn-secondary text-xs inline-flex items-center gap-1"
          >
            <Plus size={11} /> New survey
          </button>
          {loading && <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Loader2 size={10} className="animate-spin" />Loading…</div>}
          {!loading && surveys.length === 0 && (
            <div className="text-[11px] text-muted-foreground border border-dashed border-border rounded-sm px-3 py-4 text-center">
              No surveys yet for this campaign.
            </div>
          )}
          {surveys.map((s) => (
            <div key={s.id} data-testid={`survey-row-${s.id}`} className="border border-border rounded-sm p-3 bg-secondary/10">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{s.title}</span>
                    <StatusBadge status={s.status} />
                    <span className="text-[10px] text-muted-foreground">{(s.questions || []).length} question{(s.questions || []).length === 1 ? "" : "s"}</span>
                    <span className="text-[10px] text-primary">{s.response_count} response{s.response_count === 1 ? "" : "s"}</span>
                  </div>
                  {s.description && <p className="text-[11px] text-muted-foreground mt-1">{s.description}</p>}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button onClick={() => { setEditingId(s.id); setEditorOpen(true); }} data-testid={`survey-edit-${s.id}`} className="text-[11px] inline-flex items-center gap-0.5 text-primary hover:underline">
                    <Pencil size={10} /> Edit
                  </button>
                  {s.status === "draft" && (
                    <button onClick={() => publish(s.id)} data-testid={`survey-publish-${s.id}`} className="text-[11px] inline-flex items-center gap-0.5 text-primary hover:underline">
                      <Send size={10} /> Publish
                    </button>
                  )}
                  {s.public_token && (
                    <button onClick={() => copyLink(s)} data-testid={`survey-copy-link-${s.id}`} className="text-[11px] inline-flex items-center gap-0.5 text-primary hover:underline">
                      <Link2 size={10} /> Copy link
                    </button>
                  )}
                  <button onClick={() => setResponsesFor(s)} data-testid={`survey-responses-${s.id}`} className="text-[11px] inline-flex items-center gap-0.5 text-primary hover:underline">
                    <Eye size={10} /> Responses
                  </button>
                  {s.public_token && (
                    <button onClick={() => setInvitationsFor(s)} data-testid={`survey-invitations-${s.id}`} className="text-[11px] inline-flex items-center gap-0.5 text-primary hover:underline">
                      <Mail size={10} /> Invitations
                    </button>
                  )}
                  {s.status !== "archived" && (
                    <button onClick={() => archive(s.id)} data-testid={`survey-archive-${s.id}`} className="text-[11px] inline-flex items-center gap-0.5 text-muted-foreground hover:text-foreground">
                      <Archive size={10} /> Archive
                    </button>
                  )}
                  <button onClick={() => remove(s.id)} data-testid={`survey-delete-${s.id}`} className="text-[11px] inline-flex items-center gap-0.5 text-red-600 hover:underline">
                    <Trash2 size={10} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editorOpen && (
        <SurveyEditorDialog
          campaignId={campaign.id}
          surveyId={editingId}
          onClose={() => { setEditorOpen(false); setEditingId(null); }}
          onSaved={() => { setEditorOpen(false); setEditingId(null); load(); }}
        />
      )}
      {responsesFor && (
        <SurveyResponsesDialog
          survey={responsesFor}
          onClose={() => setResponsesFor(null)}
        />
      )}
      {invitationsFor && (
        <SurveyInvitationsDialog
          survey={invitationsFor}
          onClose={() => setInvitationsFor(null)}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const m = {
    draft:     "bg-secondary text-muted-foreground",
    published: "bg-primary/10 text-primary border border-primary/30",
    archived:  "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
  };
  return <span className={`text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-sm ${m[status] || m.draft}`}>{status}</span>;
}

// ─────────────────────── Editor dialog ───────────────────────
function SurveyEditorDialog({ campaignId, surveyId, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!surveyId);

  useEffect(() => {
    if (!surveyId) return;
    (async () => {
      try {
        const { data } = await api.get(`/surveys/${surveyId}`);
        setTitle(data.title); setDescription(data.description || ""); setQuestions(data.questions || []);
      } catch (e) { toast.error("Failed to load survey"); }
      finally { setLoading(false); }
    })();
  }, [surveyId]);

  const addQuestion = (type = "short_text") => {
    setQuestions((qs) => [...qs, { id: cryptoRandomId(), type, prompt: "", required: false, options: [] }]);
  };
  const updateQuestion = (idx, patch) => {
    setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };
  const removeQuestion = (idx) => setQuestions((qs) => qs.filter((_, i) => i !== idx));
  const moveQuestion = (idx, dir) => {
    setQuestions((qs) => {
      const next = [...qs];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return next;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    if (questions.length === 0) { toast.error("Add at least one question"); return; }
    for (const q of questions) {
      if (!q.prompt.trim()) { toast.error("All questions need a prompt"); return; }
      if ((q.type === "single_choice" || q.type === "multi_choice") && (q.options || []).length < 2) {
        toast.error(`"${q.prompt}" needs at least 2 options`);
        return;
      }
    }
    setSaving(true);
    try {
      if (surveyId) {
        await api.put(`/surveys/${surveyId}`, { title, description, questions });
        toast.success("Survey updated");
      } else {
        await api.post(`/campaigns/${campaignId}/surveys`, { title, description, questions });
        toast.success("Survey created");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="survey-editor-backdrop">
      <div onClick={(e) => e.stopPropagation()} data-testid="survey-editor-dialog" className="bg-card border border-border rounded-sm w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <div className="meta-label">Survey editor</div>
            <h3 className="font-display text-lg font-bold mt-0.5">{surveyId ? "Edit survey" : "Create survey"}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-sm" data-testid="survey-editor-close"><X size={14} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" />Loading…</div>
          ) : (
            <>
              <div>
                <label className="meta-label">Title</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Q4 Customer Feedback" className="form-input w-full mt-1" data-testid="survey-title-input" />
              </div>
              <div>
                <label className="meta-label">Description (optional)</label>
                <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="form-input w-full mt-1 text-sm" data-testid="survey-description-input" />
              </div>

              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between">
                  <label className="meta-label">Questions ({questions.length})</label>
                </div>
                <div className="mt-3 space-y-3">
                  {questions.map((q, i) => (
                    <QuestionRow key={q.id} idx={i} total={questions.length} q={q}
                      onChange={(patch) => updateQuestion(i, patch)}
                      onRemove={() => removeQuestion(i)}
                      onMove={(dir) => moveQuestion(i, dir)}
                    />
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className="meta-label self-center mr-1">Add question:</span>
                  {Q_TYPES.map((t) => (
                    <button key={t.key} onClick={() => addQuestion(t.key)} data-testid={`add-q-${t.key}`} className="btn-secondary text-[11px] inline-flex items-center gap-1">
                      <Plus size={10} /> {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="p-4 border-t border-border flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="survey-editor-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-1 disabled:opacity-50" data-testid="survey-editor-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {saving ? "Saving…" : "Save survey"}
          </button>
        </div>
      </div>
    </div>
  );
}

function QuestionRow({ idx, total, q, onChange, onRemove, onMove }) {
  const showOptions = q.type === "single_choice" || q.type === "multi_choice";
  return (
    <div data-testid={`question-row-${idx}`} className="border border-border rounded-sm p-3 bg-secondary/10">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-xs text-muted-foreground">Q{idx + 1}</span>
        <select value={q.type} onChange={(e) => onChange({ type: e.target.value })} className="form-input text-xs" data-testid={`q-type-${idx}`}>
          {Q_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <label className="text-[11px] inline-flex items-center gap-1 select-none">
          <input type="checkbox" checked={!!q.required} onChange={(e) => onChange({ required: e.target.checked })} /> Required
        </label>
        <div className="flex-1" />
        <button onClick={() => onMove(-1)} disabled={idx === 0} className="text-[11px] text-muted-foreground disabled:opacity-30" data-testid={`q-up-${idx}`}>▲</button>
        <button onClick={() => onMove(1)} disabled={idx === total - 1} className="text-[11px] text-muted-foreground disabled:opacity-30" data-testid={`q-down-${idx}`}>▼</button>
        <button onClick={onRemove} className="text-[11px] text-red-600" data-testid={`q-remove-${idx}`}><Trash2 size={11} /></button>
      </div>
      <input
        value={q.prompt}
        onChange={(e) => onChange({ prompt: e.target.value })}
        placeholder="Question prompt…"
        className="form-input w-full mt-2 text-sm"
        data-testid={`q-prompt-${idx}`}
      />
      {showOptions && (
        <div className="mt-2 space-y-1.5">
          {(q.options || []).map((opt, oi) => (
            <div key={oi} className="flex items-center gap-2">
              <input
                value={opt}
                onChange={(e) => {
                  const next = [...q.options]; next[oi] = e.target.value;
                  onChange({ options: next });
                }}
                placeholder={`Option ${oi + 1}`}
                className="form-input flex-1 text-xs"
                data-testid={`q-option-${idx}-${oi}`}
              />
              <button onClick={() => onChange({ options: q.options.filter((_, j) => j !== oi) })} className="text-red-600" data-testid={`q-option-remove-${idx}-${oi}`}><X size={11} /></button>
            </div>
          ))}
          <button onClick={() => onChange({ options: [...(q.options || []), ""] })} className="btn-secondary text-[11px] inline-flex items-center gap-1" data-testid={`q-option-add-${idx}`}>
            <Plus size={10} /> Add option
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Responses viewer ───────────────────────
function SurveyResponsesDialog({ survey, onClose }) {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("list"); // list | summary

  useEffect(() => {
    (async () => {
      try {
        const [{ data: list }, { data: sum }] = await Promise.all([
          api.get(`/surveys/${survey.id}/responses`),
          api.get(`/surveys/${survey.id}/responses/summary`),
        ]);
        setData(list); setSummary(sum);
      } catch (e) { toast.error(e.response?.data?.detail || "Failed to load responses"); }
    })();
  }, [survey.id]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="survey-responses-backdrop">
      <div onClick={(e) => e.stopPropagation()} data-testid="survey-responses-dialog" className="bg-card border border-border rounded-sm w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <div className="meta-label">Responses</div>
            <h3 className="font-display text-lg font-bold mt-0.5">{survey.title}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{data ? `${data.count} response${data.count === 1 ? "" : "s"}` : "Loading…"}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-sm" data-testid="survey-responses-close"><X size={14} /></button>
        </div>
        <div className="px-5 pt-3 border-b border-border flex gap-2">
          <button onClick={() => setTab("list")} data-testid="responses-tab-list" className={`text-xs px-3 py-1.5 border rounded-sm inline-flex items-center gap-1 ${tab === "list" ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
            <FileText size={11} /> Individual
          </button>
          <button onClick={() => setTab("summary")} data-testid="responses-tab-summary" className={`text-xs px-3 py-1.5 border rounded-sm inline-flex items-center gap-1 ${tab === "summary" ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
            <BarChart3 size={11} /> Aggregated
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {!data ? (
            <div className="text-xs text-muted-foreground inline-flex items-center gap-1"><Loader2 size={11} className="animate-spin" />Loading…</div>
          ) : tab === "list" ? (
            data.responses.length === 0 ? (
              <div className="text-xs text-muted-foreground text-center py-8">No responses yet.</div>
            ) : (
              <div className="space-y-3">
                {data.responses.map((r) => (
                  <div key={r.id} data-testid={`response-row-${r.id}`} className="border border-border rounded-sm p-3">
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {r.respondent_email ? (
                          <span data-testid={`response-email-${r.id}`} className="font-mono text-xs text-foreground break-all">
                            {r.respondent_email}
                          </span>
                        ) : (
                          <span className="font-mono text-xs text-muted-foreground italic">(anonymous)</span>
                        )}
                        {r.respondent_source && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm badge-info">
                            {r.respondent_source}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap">
                        <span>Started: {new Date(r.started_at).toLocaleString()}</span>
                        <span>{r.completed_at ? `✓ Completed ${new Date(r.completed_at).toLocaleString()}` : "Partial"}</span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      {(data.survey.questions || []).map((q) => (
                        <div key={q.id} className="text-xs">
                          <div className="meta-label text-[9px]">{q.prompt}</div>
                          <div className="font-mono">{formatAnswer(r.answers?.[q.id])}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            !summary ? <div className="text-xs text-muted-foreground">Loading…</div> :
            <div className="space-y-4">
              {summary.questions.map((q) => (
                <div key={q.question_id} data-testid={`summary-row-${q.question_id}`} className="border border-border rounded-sm p-3">
                  <div className="meta-label">{q.type}</div>
                  <div className="font-semibold text-sm">{q.prompt}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{q.answered_count} answered</div>
                  {q.histogram && (
                    <div className="mt-2 space-y-1">
                      {Object.entries(q.histogram).sort((a,b) => b[1]-a[1]).map(([k,v]) => (
                        <div key={k} className="flex items-center gap-2 text-xs">
                          <span className="w-32 truncate">{k}</span>
                          <div className="flex-1 h-2 bg-secondary rounded-sm overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${Math.round((v / q.answered_count) * 100)}%` }} />
                          </div>
                          <span className="font-mono text-[10px] w-12 text-right">{v} ({Math.round((v / q.answered_count) * 100)}%)</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {typeof q.average === "number" && (
                    <div className="mt-1 text-xs"><strong>Average: {q.average}</strong></div>
                  )}
                  {q.recent_values && (
                    <ul className="mt-2 list-disc list-inside text-xs space-y-0.5">
                      {q.recent_values.map((v, i) => <li key={i}>{v}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatAnswer(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

function cryptoRandomId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}

// ─────────────────────────── Survey Invitations ──────────────────────────
// Per-recipient tracked links. Admin pastes emails → each gets a unique
// `/survey/i/{token}` URL that auto-fills the recipient's email and marks
// `opened_at` + `completed_at` server-side.
function SurveyInvitationsDialog({ survey, onClose }) {
  const [invites, setInvites] = useState([]);
  const [totals, setTotals] = useState({ pending: 0, opened: 0, completed: 0, bounced: 0 });
  const [loading, setLoading] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [source, setSource] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/surveys/${survey.id}/invitations`);
      setInvites(data.invitations || []);
      setTotals(data.totals || { pending: 0, opened: 0, completed: 0, bounced: 0 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invitations");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [survey.id]);

  // 10s poll so admin sees opened/completed status flips live as recipients
  // interact with the link.
  useEffect(() => {
    const id = setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => clearInterval(id);
    /* eslint-disable-next-line */
  }, []);

  const create = async () => {
    const emails = bulkText.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
    if (!emails.length) return toast.error("Paste at least one email.");
    setCreating(true);
    try {
      const { data } = await api.post(`/surveys/${survey.id}/invitations/bulk`,
        { emails, campaign_source: source });
      toast.success(`Created ${data.created_count} · Skipped ${data.skipped_count}`);
      setBulkText("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk create failed");
    } finally { setCreating(false); }
  };

  const revoke = async (iid) => {
    if (!window.confirm("Revoke this invitation? The link will stop working immediately.")) return;
    try {
      await api.delete(`/surveys/${survey.id}/invitations/${iid}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Revoke failed"); }
  };

  const linkFor = (inv) => `${window.location.origin}/survey/i/${inv.invitation_token}`;
  const copyOne = (inv) => {
    navigator.clipboard?.writeText(linkFor(inv));
    toast.success(`Link copied for ${inv.email}`);
  };
  const copyAllCsv = () => {
    const rows = [["email", "status", "link"]];
    invites.forEach((i) => rows.push([i.email, i.status, linkFor(i)]));
    const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    navigator.clipboard?.writeText(csv);
    toast.success(`Copied ${invites.length} rows as CSV`);
  };

  const STATUS_COLOR = {
    pending: "bg-slate-100 text-slate-700",
    opened: "bg-blue-100 text-blue-800",
    completed: "bg-green-100 text-green-800",
    bounced: "bg-red-100 text-red-800",
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-sm shadow-lg max-w-4xl w-full max-h-[90vh] overflow-auto" data-testid="survey-invitations-dialog">
        <header className="px-4 py-3 border-b border-border flex items-center justify-between" style={{ backgroundColor: "#2A2EB0" }}>
          <div>
            <h3 className="text-sm font-bold text-white">Invitations — {survey.title}</h3>
            <div className="text-[10px] text-white/70 mt-0.5">
              Pending {totals.pending} · Opened {totals.opened} · Completed {totals.completed} · Bounced {totals.bounced}
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="invitations-close"><X size={14} /></button>
        </header>
        <div className="p-4 space-y-4">
          {/* Bulk-add panel */}
          <div className="border border-border rounded-sm p-3 bg-secondary/20">
            <div className="meta-label mb-2">Add recipients (one email per line, or comma-separated)</div>
            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={4}
              placeholder="jane@acme.com&#10;rohan@sap.com&#10;priya@xyz.com"
              className="w-full text-xs border border-border rounded-sm px-2 py-1.5 font-mono"
              data-testid="invitations-bulk-textarea"
            />
            <div className="flex gap-2 mt-2 items-center">
              <input
                type="text"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                placeholder="campaign_source (optional, utm-style)"
                className="flex-1 text-xs border border-border rounded-sm px-2 py-1.5"
                data-testid="invitations-source"
              />
              <button onClick={create} disabled={creating || !bulkText.trim()} className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid="invitations-create">
                {creating ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Generate links
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">
              Mailjet tip: paste the CSV below into a Mailjet campaign and merge per-row using the <code>link</code> column.
            </p>
          </div>

          {/* Table */}
          <div className="flex items-center justify-between">
            <div className="meta-label">Invitations ({invites.length})</div>
            <div className="flex gap-2">
              <button onClick={load} disabled={loading} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid="invitations-refresh">
                <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
              <button onClick={copyAllCsv} disabled={!invites.length} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid="invitations-copy-csv">
                <Copy size={11} /> Copy all as CSV
              </button>
            </div>
          </div>
          <div className="border border-border rounded-sm overflow-auto max-h-[40vh]">
            <table className="w-full text-xs">
              <thead className="bg-secondary/40 sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5 font-semibold">Email</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Status</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Opened</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Completed</th>
                  <th className="text-left px-2 py-1.5 font-semibold">Source</th>
                  <th className="text-right px-2 py-1.5 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.length === 0 && (
                  <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No invitations yet.</td></tr>
                )}
                {invites.map((i) => (
                  <tr key={i.id} className="border-t border-border hover:bg-secondary/20" data-testid={`invitation-row-${i.id}`}>
                    <td className="px-2 py-1.5 font-mono">{i.email}</td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${STATUS_COLOR[i.status] || ""}`}>
                        {i.status}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground">{i.opened_at ? new Date(i.opened_at).toLocaleString() : "—"}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{i.completed_at ? new Date(i.completed_at).toLocaleString() : "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-muted-foreground">{i.campaign_source || "—"}</td>
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <button onClick={() => copyOne(i)} className="text-primary hover:underline mr-3" data-testid={`invitation-copy-${i.id}`}>
                        <Copy size={11} className="inline" /> Copy
                      </button>
                      <button onClick={() => revoke(i.id)} className="text-red-600 hover:underline" data-testid={`invitation-revoke-${i.id}`}>
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

